"use client";

import * as React from "react";
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { mergeProps } from "@base-ui/react/merge-props";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";
import { z } from "zod";

const Dialog = DialogPrimitive.Root;

function DialogTrigger({
  render,
  nativeButton,
  ...props
}: Omit<DialogPrimitive.Trigger.Props, "render"> & {
  render?: React.ReactNode;
}) {
  const rendersNativeButton =
    render == null ||
    (React.isValidElement(render) &&
      (render.type === "button" || !z.string().safeParse(render.type).success));

  if (
    nativeButton === false &&
    React.isValidElement<React.ComponentPropsWithRef<"div">>(render)
  ) {
    const triggerElement = render;

    return (
      <DialogPrimitive.Trigger
        nativeButton={false}
        render={(triggerProps) => {
          // When a dialog trigger is composed with another interactive
          // primitive (such as a menu item), preserve that primitive's role.
          const { role: _role, ...triggerPropsWithoutRole } = triggerProps;
          const renderedProps = mergeProps(
            triggerPropsWithoutRole,
            triggerElement.props,
          );

          return React.cloneElement(triggerElement, {
            ...renderedProps,
            ref: triggerProps.ref,
          });
        }}
        {...props}
      />
    );
  }

  return (
    <DialogPrimitive.Trigger
      render={React.isValidElement(render) ? render : undefined}
      nativeButton={nativeButton ?? rendersNativeButton}
      {...props}
    />
  );
}

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Backdrop>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Backdrop>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Backdrop
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 overflow-y-auto overscroll-contain bg-black/20 backdrop-blur-xs transition-opacity data-ending-style:opacity-0 data-starting-style:opacity-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = "DialogOverlay";

type DialogContentProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Popup
> & {
  closeDisabled?: boolean;
  disableScaleAnimation?: boolean;
};

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Popup>,
  DialogContentProps
>(
  (
    {
      className,
      children,
      closeDisabled = false,
      disableScaleAnimation = false,
      ...props
    },
    ref,
  ) => (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Viewport className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto overscroll-contain">
        <DialogPrimitive.Popup
          ref={ref}
          className={cn(
            "bg-background relative z-50 my-8 grid w-full max-w-lg gap-4 rounded-xl p-6 shadow-lg transition-[opacity,scale] duration-200 data-ending-style:opacity-0 data-starting-style:opacity-0",
            !disableScaleAnimation &&
              "data-ending-style:scale-95 data-starting-style:scale-95",
            className,
          )}
          {...props}
        >
          {children}
          <DialogPrimitive.Close
            disabled={closeDisabled}
            className="ring-offset-background focus:ring-ring data-open:bg-accent data-open:text-muted-foreground absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none disabled:opacity-40"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Popup>
      </DialogPrimitive.Viewport>
    </DialogPortal>
  ),
);
DialogContent.displayName = "DialogContent";

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg leading-none font-semibold tracking-tight",
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-muted-foreground text-sm", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
